import { useEffect, useState } from 'react';
import EntitlementApi from '../../../../core/apis/EntitlementApi';
import useNotifyServerError from '../../../../core/hooks/useNotifyServerError';
import { isEmpty, uniq } from 'lodash';
import FundFamilyApi from '../../../../core/apis/FundFamilyApi';
import { useDataProvider } from 'react-admin';
import FundInvestorApi from '../../../../core/apis/FundInvestorApi';

export const createUserAuthorizations = (
  userAuthorizations,
  createdContactId
) => {
  return (userAuthorizations || []).map(authorization => ({
    ...authorization,
    // id: investorId:fundId:groupId
    id: createdContactId
      ? `${createdContactId}:${authorization.investorId}:${authorization.fundId}:${authorization.groupId}`
      : `${authorization.investorId}:${authorization.fundId}:${authorization.groupId}`
  }));
};

export const setSearch = (setState, contacts, e) => {
  const searchInput = e.target.value;
  if (searchInput.length > 0) {
    const tempFilteredContacts = [];
    contacts.forEach(contact => {
      const { firstName, lastName, email, userId, status } = contact;
      const fullName = `${firstName} ${lastName}`;
      if (
        fullName.toLowerCase().includes(searchInput.toLowerCase()) ||
        email.toLowerCase().includes(searchInput.toLowerCase()) ||
        userId.toLowerCase().includes(searchInput.toLowerCase()) ||
        status?.toLowerCase().includes(searchInput.toLowerCase())
      ) {
        tempFilteredContacts.push(contact);
      }
    });
    setState(tempFilteredContacts);
  } else {
    setState(contacts);
  }
};

/**
 * A custom React hook that fetches authorizations for the `<UserAuthorizationDatagrid />` based on the provided parameters.
 *
 * @param {string} contactId - The ID of the contact for which authorizations will be fetched.
 * @param {string} createdContactId - The ID of the created contact associated with the authorizations.
 * @param {string} selectedAssignmentMode - The selected assignment mode, e.g., 'import'.
 * @param {boolean} isCreatedContactRcOrIa - A boolean indicating whether the created contact is an RC (Regular Contact) or IA (Investor Admin).
 * @param {boolean} arePermissionGroupsUploaded - A boolean indicating whether permission groups are uploaded for the contact created.
 * @param {boolean} isNewImportToDo - A boolean indicating whether a new import is to be performed.
 *
 * @returns {Object} An object containing the fetched authorizations and their IDs.
 * @property {Array} authorizations - An array of fetched authorizations for the datagrid.
 * @property {Array} authorizationsIds - An array of IDs of the fetched authorizations.
 */

export const useFetchAuthorizationsForDatagrid = (
  contactId,
  createdContactId,
  selectedAssignmentMode,
  isCreatedContactRcOrIa,
  arePermissionGroupsUploaded,
  isNewImportToDo
) => {
  const [authorizations, setAuthorizations] = useState([]);
  const [authorizationsIds, setAuthorizationsIds] = useState([]);
  const notifyServerError = useNotifyServerError();
  const dataProvider = useDataProvider();
  useEffect(() => {
    let auth = [];

    if (!isEmpty(contactId) && arePermissionGroupsUploaded && isNewImportToDo) {
      EntitlementApi.getAuthorizationsByUserId(contactId)
        .then(fetchedAuthorizations => {
          const ids = fetchedAuthorizations.map(
            fetchedAuthorization =>
              `${createdContactId}:${fetchedAuthorization.investorId}:${fetchedAuthorization.fundId}:${fetchedAuthorization.groupId}`
          );
          setAuthorizationsIds(ids);
          const uniqFundIds = uniq(
            fetchedAuthorizations.map(item => item.fundId)
          );

          if (isCreatedContactRcOrIa && !isEmpty(uniqFundIds)) {
            FundInvestorApi.getByFundIds(uniqFundIds)
              .then(fundInvestors => {
                const fundMap = new Map(
                  fundInvestors.map(i => [i.fundId, i.fundName])
                );
                const investorMap = new Map(
                  fundInvestors.map(i => [i.investorId, i.investorName])
                );
                auth = fetchedAuthorizations.map(item => {
                  const fundName = fundMap.get(item.fundId);
                  const investorName = investorMap.get(item.investorId);
                  return {
                    ...item,
                    id: `${createdContactId}:${item.investorId}:${item.fundId}:${item.groupId}`,
                    fundName,
                    investorName,
                    userId: createdContactId
                  };
                });
                setAuthorizations(auth);
              })
              .catch(e => notifyServerError(e));
          } else {
            dataProvider
              .getMany('funds', {
                ids: uniqFundIds,
                target: 'id'
              })
              .then(({ data: funds }) => {
                if (funds.some(fund => fund.fundFamilyId !== null)) {
                  FundFamilyApi.getAll()
                    .then(({ pageData: fundFamilies }) => {
                      auth = fetchedAuthorizations.map(item => {
                        const fund = funds.find(
                          fundItem => fundItem.id === item.fundId
                        );
                        const fundFamily =
                          fund &&
                          fundFamilies.find(ff => ff.id === fund.fundFamilyId);
                        return {
                          ...item,
                          fundName: fund.name,
                          fundFamilyName: fundFamily?.name,
                          fundFamilyId: fundFamily?.id,
                          userId: contactId,
                          id: `${createdContactId}:${item.investorId}:${item.fundId}:${item.groupId}`
                        };
                      });
                      setAuthorizations(auth);
                    })
                    .catch(e => {
                      notifyServerError(e);
                    });
                } else {
                  auth = fetchedAuthorizations.map(authItem => {
                    const fund = funds.find(
                      fundFind => fundFind.id === authItem.fundId
                    );
                    return {
                      ...authItem,
                      fundName: fund.name,
                      userId: contactId,
                      id: `${createdContactId}:${authItem.investorId}:${authItem.fundId}:${authItem.groupId}`
                    };
                  });
                  setAuthorizations(auth);
                }
              })
              .catch(e => {
                notifyServerError(e);
              });
          }
        })
        .catch(e => {
          notifyServerError(e);
        });
    }
  }, [
    contactId,
    createdContactId,
    notifyServerError,
    dataProvider,
    selectedAssignmentMode,
    isCreatedContactRcOrIa,
    arePermissionGroupsUploaded,
    isNewImportToDo
  ]);

  return { authorizations, authorizationsIds };
};
